/**
 * Landing-page configuration.
 *
 * The repo ships with two complete landing pages:
 *
 *   - "custom"    — a generic agency-CRM landing the buyer brands as
 *     their own. THIS IS THE DEFAULT — every new clone should be
 *     branded for the buyer's business, so the custom variant renders
 *     at "/" out of the box and CUSTOM_BRAND below should be edited
 *     first.
 *
 *     Wired for a DONE-FOR-YOU sales motion, not self-serve SaaS:
 *     prospects see a "Talk to us" mailto CTA (uses CUSTOM_BRAND.
 *     supportEmail), the owner takes payment off-system, provisions
 *     a sub-account, then invites the client via the in-app invite
 *     flow. Pricing tiers + section are hidden by default — see the
 *     CUSTOM_BRAND.pricing block below for how to re-enable real
 *     self-serve resale.
 *
 *   - "demo" — the platform-branded marketing landing that sells the
 *     product itself (template/demo deployments).
 *
 * Flip LANDING_VARIANT below to swap which one renders at "/".
 */

export type LandingVariant = "demo" | "custom";

function resolveLandingVariant(): LandingVariant {
  const fromEnv = process.env.LANDING_VARIANT?.trim();
  if (fromEnv === "demo") return "demo";
  if (fromEnv === "custom") return "custom";
  return "custom";
}

export const LANDING_VARIANT: LandingVariant = resolveLandingVariant();

/** True when the demo/template marketing landing is active. */
export const IS_DEMO_LANDING = LANDING_VARIANT === "demo";

export interface CustomPricingTier {
  name: string;
  priceMonthly: number;
  priceAnnual: number;
  blurb: string;
  features: readonly string[];
  cta: string;
  highlighted: boolean;
}

export interface CustomBrand {
  name: string;
  tagline: string;
  shortDescription: string;
  supportEmail: string;
  primaryDomain: string;
  pricing: {
    starter: CustomPricingTier;
    pro: CustomPricingTier;
    scale: CustomPricingTier;
  };
}

/**
 * The brand object actually passed to the custom landing components at
 * render time. Resolved on the server by lib/landing/resolve-brand.ts —
 * agency doc fields take precedence, CUSTOM_BRAND fills the gaps. `logoUrl`
 * is nullable because "no logo set" is a meaningful state (renders the
 * default gradient mark instead of an <img>).
 */
export interface ResolvedBrand {
  name: string;
  logoUrl: string | null;
  tagline: string;
  shortDescription: string;
  supportEmail: string;
  primaryDomain: string;
}

/**
 * Brand fields used by the "custom" landing variant. Ignored when
 * IS_DEMO_LANDING is true. Edit these to brand the white-label landing
 * landing for your own business — the values below are placeholder
 * defaults so the page renders cleanly out of the box.
 */
export const CUSTOM_BRAND: CustomBrand = {
  /** Displayed in navbar, hero, footer copyright, page title — everywhere. */
  name: "Reclaim",

  /** One-line positioning, surfaced in hero subtitle + meta description. */
  tagline: "The AI-powered CRM that works while you sleep",

  /**
   * Short (~140 char) description used under the hero headline. Should
   * read like a tweet — what the product does, for whom.
   */
  shortDescription:
    "Contacts, pipeline, and a website builder — plus AI agents that answer every lead by chat, text, and phone the moment it lands, 24/7. One branded workspace your team actually wants to use.",

  /** Used on CTA buttons + the FAQ "talk to us" line + footer. */
  supportEmail: "hello@yourbrand.com",

  /** Used in footer, og:url, canonical. No https://, no trailing slash. */
  primaryDomain: "yourbrand.com",

  /**
   * Pricing tiers. HIDDEN BY DEFAULT — the custom landing is wired for
   * done-for-you sales (see header comment), not self-serve, so the
   * Pricing section and the #pricing nav link are not rendered. The
   * config below is kept as a starting point for buyers who later want
   * to enable real Stripe-driven SaaS resale. To re-enable:
   *
   *   1. In src/app/page.tsx, re-import and render <CustomPricing />
   *      (file at src/components/landing-custom/pricing.tsx).
   *   2. Re-add the "#pricing" nav link in landing-custom/navbar.tsx
   *      (desktop nav + mobile sheet).
   *   3. Wire the pricing card buttons to createCheckoutSession with
   *      the relevant STRIPE_PRO_PRICE_ID etc., instead of /signup.
   *   4. Un-gate the Subscription panel in the sub-account settings
   *      page (currently gated on IS_DEMO_LANDING).
   *   5. Add a Stripe-driven public signup flow that provisions a
   *      fresh agency + sub-account + owner membership on
   *      checkout.completed — today's /api/auth/signup is invite-only
   *      after the first bootstrap user, so strangers paying through
   *      Stripe can't currently land anywhere. See CLAUDE.md
   *      ("Auth & Tenancy Model") for the existing signup contract.
   */
  pricing: {
    starter: {
      name: "Starter",
      priceMonthly: 0,
      priceAnnual: 0,
      blurb: "For solo operators kicking the tyres.",
      features: [
        "Up to 100 contacts",
        "1 sub-account",
        "Pipeline + tasks + calendar",
        "Forms with public hosted page",
        "Community support",
      ],
      cta: "Get started free",
      highlighted: false,
    },
    pro: {
      name: "Pro",
      priceMonthly: 97,
      priceAnnual: 77,
      blurb: "For growing teams with serious volume.",
      features: [
        "Unlimited contacts",
        "Unlimited sub-accounts",
        "Pipeline + Kanban + automations",
        "Email + SMS shared sender",
        "Built-in website builder",
        "Up to 5 team seats",
        "Priority email support",
      ],
      cta: "Start 14-day trial",
      highlighted: true,
    },
    scale: {
      name: "Scale",
      priceMonthly: 297,
      priceAnnual: 237,
      blurb: "For teams running multi-account workspaces.",
      features: [
        "Everything in Pro",
        "Unlimited team seats",
        "Advanced automations",
        "Audit logs + SSO",
        "Dedicated onboarding",
        "SLA-backed support",
      ],
      cta: "Talk to sales",
      highlighted: false,
    },
  },
};
